/**
 * RefundDialog -- Manage ship buy-backs: view owned ships, request buy-backs,
 * monitor the buy-back queue, and cancel pending buy-backs.
 *
 * Two tabs:
 *   "My Ships"     - lists owned ships with purchase info and buy-back request flow
 *   "Buy-Back Queue" - shows queue status, daily cap, and pending buy-backs with cancel
 */

import Phaser from 'phaser';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import type { MMOMonetizationManager, RefundEligibility } from '../../managers/MMOMonetizationManager';
import type { Purchase, Refund } from '../../shared/common/MonetizationTypes';
import { Colors, createDialogChrome, closeDialog, Fonts, FontSize } from './GameUITheme';
import { isMobile } from '../../utils/ResponsiveHelper';
import { I18n } from '../../i18n/I18nService';
const BORDER_COLOR = Colors.borderBright;
const CARD_COLOR = Colors.bgCard;
const CARD_HOVER_COLOR = Colors.bgCardHover;
const CARD_SELECTED_COLOR = 0x253a66;
const TAB_ACTIVE_COLOR = 0x2a4477;
const TAB_INACTIVE_COLOR = 0x152030;
const ACCENT_COLOR = 0xff8844;
const EXIT_FEE_PCT = 4.25;

type TabName = 'my_ships' | 'refund_queue';

export class RefundDialog extends Phaser.GameObjects.Container {
  private dialogW: number;
  private dialogH: number;
  private monetization: MMOMonetizationManager;
  private activeTab: TabName = 'my_ships';

  // Data
  private purchases: Purchase[] = [];
  private selectedPurchase: Purchase | null = null;
  private queuedRefunds: Refund[] = [];
  private queueInfo: any | null = null;

  // UI containers
  private tabContainer: Phaser.GameObjects.Container;
  private contentContainer: Phaser.GameObjects.Container;
  private detailContainer: Phaser.GameObjects.Container;
  private statusText: Phaser.GameObjects.Text;
  private isProcessing = false;

  constructor(scene: Phaser.Scene) {
    const mobile = isMobile(scene);
    const DIALOG_WIDTH = mobile ? 340 : 652;
    const DIALOG_HEIGHT = mobile ? 540 : 560;

    const { width, height } = scene.cameras.main;
    super(scene, width / 2 - DIALOG_WIDTH / 2, height / 2 - DIALOG_HEIGHT / 2);

    this.dialogW = DIALOG_WIDTH;
    this.dialogH = DIALOG_HEIGHT;

    this.monetization = ServiceLocator.get<MMOMonetizationManager>(Services.MMO_MONETIZATION);

    // Themed dialog chrome (overlay + panel + header + title + close)
    createDialogChrome(scene, this, this.dialogW, this.dialogH, {
      title: I18n.t('dialog.refund'),
      onClose: () => this.close(),
    });

    // Tab bar
    this.tabContainer = scene.add.container(0, 55);
    this.add(this.tabContainer);
    this.renderTabs();

    // Content area (left side for list)
    this.contentContainer = scene.add.container(10, 95);
    this.add(this.contentContainer);

    // Detail area (right side or bottom for estimate/queue detail)
    this.detailContainer = scene.add.container(10, 95);
    this.add(this.detailContainer);

    // Status text
    this.statusText = scene.add.text(this.dialogW / 2, this.dialogH - 15, '', {
      fontSize: FontSize.sm,
      color: '#889999',
      fontFamily: Fonts.body,
    });
    this.statusText.setOrigin(0.5);
    this.add(this.statusText);

    this.setDepth(2000);
    scene.add.existing(this);

    this.loadTabData();
  }

  private close(): void {
    closeDialog(this.scene, this);
  }

  // ============ Tabs ============

  private renderTabs(): void {
    this.tabContainer.removeAll(true);

    const tabs: { key: TabName; label: string }[] = [
      { key: 'my_ships', label: 'My Ships' },
      { key: 'refund_queue', label: 'Buy-Back Queue' },
    ];

    const tabWidth = this.dialogW / tabs.length;

    tabs.forEach((tab, i) => {
      const isActive = this.activeTab === tab.key;
      const x = i * tabWidth;

      const tabBg = this.scene.add.rectangle(x, 0, tabWidth, 32, isActive ? TAB_ACTIVE_COLOR : TAB_INACTIVE_COLOR);
      tabBg.setOrigin(0, 0);
      tabBg.setStrokeStyle(1, isActive ? ACCENT_COLOR : BORDER_COLOR);
      this.tabContainer.add(tabBg);

      const tabText = this.scene.add.text(x + tabWidth / 2, 16, tab.label, {
        fontSize: FontSize.sm,
        color: isActive ? '#ffffff' : '#778899',
        fontFamily: isActive ? Fonts.heading : Fonts.body,
      });
      tabText.setOrigin(0.5);
      this.tabContainer.add(tabText);

      tabBg.setInteractive();
      tabBg.on('pointerdown', () => {
        this.activeTab = tab.key;
        this.renderTabs();
        this.loadTabData();
      });
    });
  }

  private async loadTabData(): Promise<void> {
    this.statusText.setText('Loading...');
    this.contentContainer.removeAll(true);
    this.detailContainer.removeAll(true);
    this.selectedPurchase = null;

    switch (this.activeTab) {
      case 'my_ships': {
        this.purchases = await this.monetization.fetchPurchaseHistory();
        // Filter to active ship purchases only
        const activeShips = this.purchases.filter(
          (p) => p.assetType === 'ship' && p.status === 'active',
        );
        this.statusText.setText(`${activeShips.length} active ship(s)`);
        this.renderMyShips(activeShips);
        break;
      }
      case 'refund_queue': {
        const queueData = await this.monetization.fetchRefundQueue();
        this.queueInfo = queueData.queue;
        this.queuedRefunds = queueData.userQueuedRefunds;
        this.statusText.setText(`${this.queuedRefunds.length} pending buy-back(s)`);
        this.renderRefundQueue();
        break;
      }
    }
  }

  // ============ My Ships Tab ============

  private renderMyShips(ships: Purchase[]): void {
    this.contentContainer.removeAll(true);
    this.detailContainer.removeAll(true);

    if (ships.length === 0) {
      const emptyText = this.scene.add.text(this.dialogW / 2 - 10, 80, 'No active ships found.\nPurchase ships from the Ship Store first.', {
        fontSize: FontSize.sm,
        color: '#667788',
        fontFamily: Fonts.body,
        align: 'center',
      });
      emptyText.setOrigin(0.5, 0);
      this.contentContainer.add(emptyText);
      return;
    }

    // Column header
    const header = this.scene.add.text(5, 0, 'Ship                 Purchased           Invested', {
      fontSize: FontSize.sm,
      color: '#667799',
      fontFamily: Fonts.body,
    });
    this.contentContainer.add(header);

    const listWidth = this.dialogW - 20;
    const cardHeight = 52;
    let yOffset = 20;

    for (const purchase of ships) {
      const card = this.createShipCard(0, yOffset, listWidth, cardHeight, purchase);
      this.contentContainer.add(card);
      yOffset += cardHeight + 4;
    }
  }

  private createShipCard(
    x: number,
    y: number,
    w: number,
    h: number,
    purchase: Purchase,
  ): Phaser.GameObjects.Container {
    const card = this.scene.add.container(x, y);

    const bg = this.scene.add.rectangle(0, 0, w, h, CARD_COLOR);
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(1, 0x334466);
    card.add(bg);

    // Ship type display name
    const displayName = I18n.shipName(purchase.assetId);

    const nameText = this.scene.add.text(10, 10, displayName, {
      fontSize: FontSize.md,
      color: '#ccddff',
      fontFamily: Fonts.heading,
    });
    nameText.setOrigin(0, 0);
    card.add(nameText);

    // Purchase date
    const purchaseDate = new Date(purchase.purchasedAt).toLocaleDateString();
    const dateText = this.scene.add.text(10, 30, `Purchased: ${purchaseDate}`, {
      fontSize: FontSize.xs,
      color: '#778899',
      fontFamily: Fonts.body,
    });
    dateText.setOrigin(0, 0);
    card.add(dateText);

    // Total invested
    const investedText = this.scene.add.text(w - 10, 10, `$${purchase.amountUsd.toFixed(2)}`, {
      fontSize: FontSize.lg,
      color: '#ffcc00',
      fontFamily: Fonts.heading,
    });
    investedText.setOrigin(1, 0);
    card.add(investedText);

    // "Request Refund" small button
    const btnW = 110;
    const btnH = 22;
    const refundBtn = this.scene.add.container(w - 10 - btnW, 28);

    const btnBg = this.scene.add.rectangle(0, 0, btnW, btnH, 0x663300);
    btnBg.setOrigin(0, 0);
    btnBg.setStrokeStyle(1, 0xff8844);
    refundBtn.add(btnBg);

    const btnText = this.scene.add.text(btnW / 2, btnH / 2, 'Request Buy-Back', {
      fontSize: FontSize.xs,
      color: '#ff8844',
      fontFamily: Fonts.heading,
    });
    btnText.setOrigin(0.5);
    refundBtn.add(btnText);

    refundBtn.setSize(btnW, btnH);
    refundBtn.setInteractive(new Phaser.Geom.Rectangle(0, 0, btnW, btnH), Phaser.Geom.Rectangle.Contains);
    refundBtn.on('pointerover', () => btnBg.setFillStyle(0x884400));
    refundBtn.on('pointerout', () => btnBg.setFillStyle(0x663300));
    refundBtn.on('pointerdown', () => this.selectShipForRefund(purchase));
    card.add(refundBtn);

    // Card hover/select interaction
    card.setSize(w, h);
    card.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);

    card.on('pointerover', () => {
      if (this.selectedPurchase !== purchase) {
        bg.setFillStyle(CARD_HOVER_COLOR);
      }
    });

    card.on('pointerout', () => {
      if (this.selectedPurchase !== purchase) {
        bg.setFillStyle(CARD_COLOR);
      }
    });

    card.on('pointerdown', () => {
      this.deselectAllCards();
      this.selectedPurchase = purchase;
      bg.setFillStyle(CARD_SELECTED_COLOR);
      bg.setStrokeStyle(2, ACCENT_COLOR);
      this.selectShipForRefund(purchase);
    });

    card.setData('bg', bg);
    card.setData('purchase', purchase);

    return card;
  }

  private deselectAllCards(): void {
    for (const child of this.contentContainer.list) {
      const cardContainer = child as Phaser.GameObjects.Container;
      const bg = cardContainer.getData('bg') as Phaser.GameObjects.Rectangle;
      if (bg) {
        bg.setFillStyle(CARD_COLOR);
        bg.setStrokeStyle(1, 0x334466);
      }
    }
  }

  private async selectShipForRefund(purchase: Purchase): Promise<void> {
    this.selectedPurchase = purchase;
    this.contentContainer.removeAll(true);
    this.detailContainer.removeAll(true);

    const displayName = I18n.shipName(purchase.assetId);
    this.statusText.setText(`Checking buy-back eligibility for ${displayName}...`);

    try {
      // Check eligibility first
      const eligibility = await this.monetization.checkRefundEligibility(purchase.assetId);

      // Fetch estimate regardless (server may return partial info)
      const estimate = await this.monetization.fetchRefundEstimate(purchase.id);

      if (this.scene) {
        this.renderRefundDetail(purchase, eligibility, estimate);
      }
    } catch (error) {
      console.error('[RefundDialog] Failed to check eligibility:', error);
      if (this.scene) {
        this.statusText.setText('Failed to check buy-back eligibility');
      }
    }
  }

  private renderRefundDetail(
    purchase: Purchase,
    eligibility: RefundEligibility,
    estimate: any,
  ): void {
    this.detailContainer.removeAll(true);

    const displayName = I18n.shipName(purchase.assetId);
    const panelX = 0;
    const panelY = 0;
    const panelW = this.dialogW - 20;
    const panelH = 210;

    // Detail panel background
    const panelBg = this.scene.add.rectangle(panelX, panelY, panelW, panelH, 0x0d1525);
    panelBg.setOrigin(0, 0);
    panelBg.setStrokeStyle(1, BORDER_COLOR);
    this.detailContainer.add(panelBg);

    // Section title
    const sectionTitle = this.scene.add.text(panelX + 10, panelY + 8, `Buy-Back Estimate: ${displayName}`, {
      fontSize: FontSize.md,
      color: '#ff8844',
      fontFamily: Fonts.heading,
    });
    this.detailContainer.add(sectionTitle);

    let infoY = panelY + 30;

    // 30-day cooldown check
    if (!eligibility.eligible && eligibility.cooldownEndsAt) {
      const cooldownDate = new Date(eligibility.cooldownEndsAt);
      const now = new Date();
      const daysLeft = Math.max(0, Math.ceil((cooldownDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

      // Cooldown warning bar
      const cooldownBg = this.scene.add.rectangle(panelX + 10, infoY, panelW - 20, 36, 0x442200);
      cooldownBg.setOrigin(0, 0);
      cooldownBg.setStrokeStyle(1, 0x884400);
      this.detailContainer.add(cooldownBg);

      const cooldownIcon = this.scene.add.text(panelX + 18, infoY + 8, '30-DAY COOLDOWN', {
        fontSize: FontSize.sm,
        color: '#ff6600',
        fontFamily: Fonts.heading,
      });
      this.detailContainer.add(cooldownIcon);

      const cooldownText = this.scene.add.text(panelX + 18, infoY + 22, `${daysLeft} day(s) remaining - Eligible on ${cooldownDate.toLocaleDateString()}`, {
        fontSize: FontSize.xs,
        color: '#cc8844',
        fontFamily: Fonts.body,
      });
      this.detailContainer.add(cooldownText);

      infoY += 42;

      this.statusText.setText(`${displayName} not yet eligible for buy-back (${daysLeft} day cooldown remaining)`);
      return;
    }

    if (!eligibility.eligible) {
      const reasonText = this.scene.add.text(panelX + 10, infoY, `Not eligible: ${eligibility.reason || 'Unknown reason'}`, {
        fontSize: FontSize.sm,
        color: '#ff4444',
        fontFamily: Fonts.body,
      });
      this.detailContainer.add(reasonText);
      this.statusText.setText(`${displayName}: Not eligible for buy-back`);
      return;
    }

    // Eligible - show estimate
    if (estimate) {
      const totalInvested = estimate.totalInvested ?? purchase.amountUsd;
      const exitFee = totalInvested * (EXIT_FEE_PCT / 100);
      const estimatedReturn = estimate.netToPlayer ?? (totalInvested - exitFee);

      // Total invested row
      this.detailContainer.add(this.createInfoRow(panelX + 10, infoY, 'Total Invested:', `$${totalInvested.toFixed(2)}`, '#ccddff'));
      infoY += 18;

      // LP tokens redeemed
      if (estimate.lpTokensRedeemed !== undefined) {
        this.detailContainer.add(this.createInfoRow(panelX + 10, infoY, 'LP Tokens Redeemed:', `${estimate.lpTokensRedeemed.toFixed(4)}`, '#aaccff'));
        infoY += 18;
      }

      // Stablecoin received
      if (estimate.stablecoinReceived !== undefined) {
        this.detailContainer.add(this.createInfoRow(panelX + 10, infoY, 'Stablecoin Received:', `$${estimate.stablecoinReceived.toFixed(2)}`, '#aaccff'));
        infoY += 18;
      }

      // STELLAR sold
      if (estimate.stellarSold !== undefined) {
        this.detailContainer.add(this.createInfoRow(panelX + 10, infoY, '$STELLAR Sold:', `${estimate.stellarSold.toFixed(2)}`, '#aaccff'));
        infoY += 18;
      }

      // Exit fee line
      const feeColor = '#ff6644';
      this.detailContainer.add(this.createInfoRow(panelX + 10, infoY, `Exit Fee (${EXIT_FEE_PCT}%):`, `-$${exitFee.toFixed(2)}`, feeColor));
      infoY += 18;

      // Separator
      const separator = this.scene.add.graphics();
      separator.lineStyle(1, BORDER_COLOR, 0.4);
      separator.lineBetween(panelX + 10, infoY + 2, panelX + panelW - 10, infoY + 2);
      this.detailContainer.add(separator);
      infoY += 8;

      // Estimated return (net to player)
      const returnLabel = this.scene.add.text(panelX + 10, infoY, 'Estimated Return:', {
        fontSize: FontSize.sm,
        color: '#aaccff',
        fontFamily: Fonts.heading,
      });
      this.detailContainer.add(returnLabel);

      const returnValue = this.scene.add.text(panelX + panelW - 10, infoY, `$${estimatedReturn.toFixed(2)}`, {
        fontSize: FontSize.lg,
        color: '#44ff66',
        fontFamily: Fonts.heading,
      });
      returnValue.setOrigin(1, 0);
      this.detailContainer.add(returnValue);
      infoY += 24;

      // Components retired notice
      if (estimate.componentsRetired && estimate.componentsRetired.length > 0) {
        const compText = this.scene.add.text(panelX + 10, infoY, `${estimate.componentsRetired.length} component(s) will be retired`, {
          fontSize: FontSize.xs,
          color: '#cc8866',
          fontFamily: Fonts.body,
        });
        this.detailContainer.add(compText);
        infoY += 16;
      }

      // Warning text
      const warningText = this.scene.add.text(panelX + 10, infoY, 'Ship and bound components will be permanently retired.', {
        fontSize: FontSize.xs,
        color: '#ff6644',
        fontFamily: Fonts.body,
      });
      this.detailContainer.add(warningText);
      infoY += 18;

      // Confirm Refund button
      const confirmBtn = this.createActionButton(
        panelX + panelW / 2,
        infoY + 4,
        'CONFIRM BUY-BACK REQUEST',
        0x663300,
        0xff8844,
        '#ff8844',
        () => this.handleRequestRefund(purchase),
      );
      this.detailContainer.add(confirmBtn);

      this.statusText.setText(`${displayName}: Eligible for buy-back - Est. return $${estimatedReturn.toFixed(2)}`);
    } else {
      const noEstimateText = this.scene.add.text(panelX + 10, infoY, 'Unable to fetch buy-back estimate. Try again later.', {
        fontSize: FontSize.sm,
        color: '#ff4444',
        fontFamily: Fonts.body,
      });
      this.detailContainer.add(noEstimateText);
      this.statusText.setText(`${displayName}: Failed to load estimate`);
    }
  }

  private createInfoRow(x: number, y: number, label: string, value: string, valueColor: string): Phaser.GameObjects.Container {
    const row = this.scene.add.container(x, y);

    const labelText = this.scene.add.text(0, 0, label, {
      fontSize: FontSize.sm,
      color: '#8899bb',
      fontFamily: Fonts.body,
    });
    row.add(labelText);

    const valueText = this.scene.add.text(this.dialogW - 40, 0, value, {
      fontSize: FontSize.sm,
      color: valueColor,
      fontFamily: Fonts.heading,
    });
    valueText.setOrigin(1, 0);
    row.add(valueText);

    return row;
  }

  private createActionButton(
    x: number,
    y: number,
    label: string,
    bgColor: number,
    strokeColor: number,
    textColor: string,
    callback: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);
    const w = 220;
    const h = 30;

    const bg = this.scene.add.rectangle(0, 0, w, h, bgColor);
    bg.setStrokeStyle(2, strokeColor);
    btn.add(bg);

    const text = this.scene.add.text(0, 0, label, {
      fontSize: FontSize.sm,
      color: textColor,
      fontFamily: Fonts.heading,
    });
    text.setOrigin(0.5);
    btn.add(text);

    btn.setSize(w, h);
    btn.setInteractive(new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h), Phaser.Geom.Rectangle.Contains);
    btn.on('pointerover', () => bg.setFillStyle(bgColor + 0x222222));
    btn.on('pointerout', () => bg.setFillStyle(bgColor));
    btn.on('pointerdown', callback);

    return btn;
  }

  private async handleRequestRefund(purchase: Purchase): Promise<void> {
    if (this.isProcessing) return;

    const displayName = I18n.shipName(purchase.assetId);
    this.isProcessing = true;
    this.statusText.setText(`Submitting buy-back request for ${displayName}...`);

    const result = await this.monetization.requestRefund(purchase.id);

    this.isProcessing = false;

    if (result.success) {
      this.statusText.setText(
        `Buy-back requested! Queue position: #${result.queuePosition ?? '?'}. ID: ${result.refundId}`,
      );
      // Refresh the data after a short delay
      this.scene.time.delayedCall(1500, () => {
        if (this.scene) {
          this.loadTabData();
        }
      });
    } else {
      this.statusText.setText(`Buy-back failed: ${result.error}`);
    }
  }

  // ============ Buy-Back Queue Tab ============

  private renderRefundQueue(): void {
    this.contentContainer.removeAll(true);
    this.detailContainer.removeAll(true);

    let yOffset = 0;

    // Queue info panel
    if (this.queueInfo) {
      const infoBg = this.scene.add.rectangle(0, yOffset, this.dialogW - 20, 80, 0x0d1525);
      infoBg.setOrigin(0, 0);
      infoBg.setStrokeStyle(1, BORDER_COLOR);
      this.contentContainer.add(infoBg);

      const queueTitle = this.scene.add.text(10, yOffset + 8, 'Daily Buy-Back Queue Status', {
        fontSize: FontSize.sm,
        color: '#ff8844',
        fontFamily: Fonts.heading,
      });
      this.contentContainer.add(queueTitle);

      // Daily cap info
      const dailyCap = this.queueInfo.dailyCap ?? 'N/A';
      const dailyProcessed = this.queueInfo.dailyProcessed ?? 0;
      const totalQueued = this.queueInfo.totalQueued ?? 0;

      const capText = this.scene.add.text(10, yOffset + 28, `Daily Cap: $${dailyCap}`, {
        fontSize: FontSize.sm,
        color: '#aaccff',
        fontFamily: Fonts.body,
      });
      this.contentContainer.add(capText);

      const processedText = this.scene.add.text(10, yOffset + 44, `Processed Today: $${dailyProcessed}`, {
        fontSize: FontSize.sm,
        color: '#88cc88',
        fontFamily: Fonts.body,
      });
      this.contentContainer.add(processedText);

      const queuedText = this.scene.add.text(10, yOffset + 60, `Total Queued: ${totalQueued} buy-back(s)`, {
        fontSize: FontSize.sm,
        color: '#cccccc',
        fontFamily: Fonts.body,
      });
      this.contentContainer.add(queuedText);

      // Cap usage bar
      if (typeof dailyCap === 'number' && dailyCap > 0) {
        const barX = 260;
        const barW = 220;
        const barH = 12;
        const barY = yOffset + 30;

        const barBg = this.scene.add.rectangle(barX, barY, barW, barH, 0x222233);
        barBg.setOrigin(0, 0);
        barBg.setStrokeStyle(1, 0x445566);
        this.contentContainer.add(barBg);

        const fillPct = Math.min(1, dailyProcessed / dailyCap);
        const fillColor = fillPct > 0.9 ? 0xff4444 : fillPct > 0.7 ? 0xffaa00 : 0x44cc44;
        if (fillPct > 0) {
          const barFill = this.scene.add.rectangle(barX, barY, barW * fillPct, barH, fillColor);
          barFill.setOrigin(0, 0);
          this.contentContainer.add(barFill);
        }

        const pctLabel = this.scene.add.text(barX + barW / 2, barY + barH / 2, `${(fillPct * 100).toFixed(1)}%`, {
          fontSize: FontSize.xs,
          color: '#ffffff',
          fontFamily: Fonts.heading,
        });
        pctLabel.setOrigin(0.5);
        this.contentContainer.add(pctLabel);

        const capLabel = this.scene.add.text(barX, barY + barH + 4, '10% of total LP TVL daily limit', {
          fontSize: FontSize.xs,
          color: '#667788',
          fontFamily: Fonts.body,
        });
        this.contentContainer.add(capLabel);
      }

      yOffset += 90;
    }

    // Separator
    const separator = this.scene.add.graphics();
    separator.lineStyle(1, BORDER_COLOR, 0.3);
    separator.lineBetween(0, yOffset, this.dialogW - 20, yOffset);
    this.contentContainer.add(separator);
    yOffset += 8;

    // Pending refunds header
    const pendingHeader = this.scene.add.text(5, yOffset, 'Your Pending Buy-Backs', {
      fontSize: FontSize.sm,
      color: '#aaccff',
      fontFamily: Fonts.heading,
    });
    this.contentContainer.add(pendingHeader);
    yOffset += 22;

    if (this.queuedRefunds.length === 0) {
      const emptyText = this.scene.add.text(5, yOffset, 'No pending buy-backs in the queue.', {
        fontSize: FontSize.sm,
        color: '#667788',
        fontFamily: Fonts.body,
      });
      this.contentContainer.add(emptyText);
      return;
    }

    // Render each queued refund
    const cardWidth = this.dialogW - 20;
    const cardHeight = 70;

    for (const refund of this.queuedRefunds) {
      const card = this.createRefundCard(0, yOffset, cardWidth, cardHeight, refund);
      this.contentContainer.add(card);
      yOffset += cardHeight + 4;
    }
  }

  private createRefundCard(
    x: number,
    y: number,
    w: number,
    h: number,
    refund: Refund,
  ): Phaser.GameObjects.Container {
    const card = this.scene.add.container(x, y);

    const bg = this.scene.add.rectangle(0, 0, w, h, CARD_COLOR);
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(1, 0x334466);
    card.add(bg);

    // Status badge
    const statusColors: Record<string, string> = {
      pending: '#ffaa00',
      queued: '#44aaff',
      processing: '#ff8844',
      completed: '#44ff66',
      failed: '#ff4444',
      cancelled: '#888888',
    };
    const statusColor = statusColors[refund.status] || '#888888';

    const statusBadge = this.scene.add.text(10, 8, refund.status.toUpperCase(), {
      fontSize: FontSize.xs,
      color: statusColor,
      fontFamily: Fonts.heading,
    });
    card.add(statusBadge);

    // Queue position
    if (refund.queuePosition !== null && refund.queuePosition !== undefined) {
      const posText = this.scene.add.text(w - 10, 8, `#${refund.queuePosition}`, {
        fontSize: FontSize.md,
        color: '#ff8844',
        fontFamily: Fonts.heading,
      });
      posText.setOrigin(1, 0);
      card.add(posText);

      const posLabel = this.scene.add.text(w - 10, 24, 'in queue', {
        fontSize: FontSize.xs,
        color: '#778899',
        fontFamily: Fonts.body,
      });
      posLabel.setOrigin(1, 0);
      card.add(posLabel);
    }

    // Invested amount
    const investedText = this.scene.add.text(10, 24, `Invested: $${refund.totalInvested.toFixed(2)}`, {
      fontSize: FontSize.sm,
      color: '#ccddff',
      fontFamily: Fonts.body,
    });
    card.add(investedText);

    // Expected return
    const exitFee = refund.exitFeeCharged ?? refund.totalInvested * (EXIT_FEE_PCT / 100);
    const netReturn = refund.netToPlayer ?? (refund.totalInvested - exitFee);
    const returnText = this.scene.add.text(10, 38, `Est. Return: $${netReturn.toFixed(2)} (after ${EXIT_FEE_PCT}% fee)`, {
      fontSize: FontSize.sm,
      color: '#88cc88',
      fontFamily: Fonts.body,
    });
    card.add(returnText);

    // Requested date
    const requestedDate = new Date(refund.requestedAt).toLocaleDateString();
    const dateText = this.scene.add.text(10, 52, `Requested: ${requestedDate}`, {
      fontSize: FontSize.xs,
      color: '#778899',
      fontFamily: Fonts.body,
    });
    card.add(dateText);

    // Cancel button (only for pending/queued)
    if (refund.status === 'pending' || refund.status === 'queued') {
      const cancelBtnW = 70;
      const cancelBtnH = 22;
      const cancelBtn = this.scene.add.container(w - 10 - cancelBtnW, h - 28);

      const cancelBg = this.scene.add.rectangle(0, 0, cancelBtnW, cancelBtnH, 0x440000);
      cancelBg.setOrigin(0, 0);
      cancelBg.setStrokeStyle(1, 0xff4444);
      cancelBtn.add(cancelBg);

      const cancelText = this.scene.add.text(cancelBtnW / 2, cancelBtnH / 2, 'Cancel', {
        fontSize: FontSize.xs,
        color: '#ff4444',
        fontFamily: Fonts.heading,
      });
      cancelText.setOrigin(0.5);
      cancelBtn.add(cancelText);

      cancelBtn.setSize(cancelBtnW, cancelBtnH);
      cancelBtn.setInteractive(new Phaser.Geom.Rectangle(0, 0, cancelBtnW, cancelBtnH), Phaser.Geom.Rectangle.Contains);
      cancelBtn.on('pointerover', () => cancelBg.setFillStyle(0x660000));
      cancelBtn.on('pointerout', () => cancelBg.setFillStyle(0x440000));
      cancelBtn.on('pointerdown', () => this.handleCancelRefund(refund));

      card.add(cancelBtn);
    }

    // Processing indicator
    if (refund.status === 'processing') {
      const processingText = this.scene.add.text(w - 10, h - 20, 'Processing...', {
        fontSize: FontSize.xs,
        color: '#ff8844',
        fontFamily: Fonts.heading,
      });
      processingText.setOrigin(1, 0);
      card.add(processingText);
    }

    return card;
  }

  private async handleCancelRefund(refund: Refund): Promise<void> {
    if (this.isProcessing) return;

    this.isProcessing = true;
    this.statusText.setText('Cancelling buy-back...');

    const result = await this.monetization.cancelRefund(refund.id);

    this.isProcessing = false;

    if (result.success) {
      this.statusText.setText('Buy-back cancelled successfully.');
      // Refresh queue data
      this.scene.time.delayedCall(500, () => {
        if (this.scene) {
          this.loadTabData();
        }
      });
    } else {
      this.statusText.setText(`Cancel failed: ${result.error}`);
    }
  }
}
